import {useTranslation} from "react-i18next";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {WarningRounded} from "@mui/icons-material";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import * as Yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import FormSelect from "../../../experiments/forms/FormSelect";
import FileUploader from "../../../../../util/component/FileUploader";
import {useInvoiceRepository} from "../../../api/repositories/InvoiceRepository";

interface LeasePlanModalInputs {
    legalEntity: string,
    dealer: string
}

const UploadLeasePlanModal = (props: any) => {
    const {t} = useTranslation('finance', {keyPrefix: 'invoices'});
    const {t: tg} = useTranslation("translation");
    const {uploadLeasePlanFile} = useInvoiceRepository();
    const {globalData: {user}, dispatchGlobalData} = useContext(GlobalContext);
    const [errorMessageBox, showErrorMessageBox] = useState<boolean>(false);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>([]);

    const schema = Yup.object().shape({
        legalEntity: Yup.string().required(t('leasePlan.messages.requiredCheck')),
        dealer: Yup.string().required(t('leasePlan.messages.requiredCheck')),
    });

    const defaultValues = () => {
        return {
            legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
            dealer: dealerOptions.length == 1 ? dealerOptions[0].value : ""
        }
    };


    const {watch, control, reset} = useForm<LeasePlanModalInputs>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues()
    });

    const onSubmit = (file: File) => {
        let fileExtension = file.name.split('.').pop();
        if (fileExtension!.toLowerCase() === "csv") {
            uploadLeasePlanFile(
                watch("dealer"),
                watch("legalEntity"),
                file
            ).then((response) => {
                if (response.status && response.status === "OK") {
                    props.onClose();
                    // setUpdating(true);
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("leasePlan.messages.fileUploaded"),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                    reset(defaultValues());
                }
            });
        } else {
            showErrorMessageBox(true);
            props.onClose();
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("leasePlan.handleErrors.invalidFileType"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            reset(defaultValues());
        }

    }
    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }

    useEffect(() => {
        handleChangeLegalEntities(legalEntityOptions[0].value)
        if (legalEntityOptions.length == 1 && dealers.length == 1) {
            setTimeout(() => {
                handleChangeLegalEntities(legalEntityOptions[0].value)
            });
        }
    }, [legalEntityOptions.length == 1])


    return (
        <Dialog fullWidth={true} open={props.open} onClose={() => {
            return;
        }}>
            <DialogTitle>
                <Typography fontWeight={"bold"} color={"primary"}>
                    {t('leasePlan.modal.title')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        props.onClose();
                    }}
                    color={"primary"}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{padding: "15px"}} alignItems={"center"}>
                    <Grid item xs={6}>
                        <FormSelect
                            name={"legalEntity"}
                            control={control}
                            props={{
                                label: tg("shared.legalEntities"),
                                fullWidth: true,
                                required: true,
                                onChange: (e: any) => {
                                    handleChangeLegalEntities(e.target.value)
                                },
                            }}
                            items={legalEntityOptions.map((option: any) => (
                                <MenuItem key={option.value}
                                          value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormSelect
                            name={"dealer"}
                            control={control}
                            props={{
                                label: tg("shared.dealers"),
                                fullWidth: true,
                                required: true,
                                disabled: dealers.length === 0
                            }}
                            items={dealers.map((option: any) => (
                                <MenuItem key={option.value}
                                          value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FileUploader
                            disabled={watch("legalEntity") === "" || watch("dealer") === ""}
                            errorMessage={t('leasePlan.handleErrors.invalidFileType')}
                            onUpload={(file: File) => {
                                onSubmit(file)
                            }}
                            acceptedFiles={{'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.csv']}}/>
                    </Grid>
                    {errorMessageBox &&
                        <Grid item xs={12}>
                            <Box style={{display: 'flex', gap: '1ch'}}>
                                <WarningRounded/>
                                <TextField
                                    color={"error"}
                                    fullWidth
                                    id="error"
                                    disabled={true}
                                    value={t("leasePlan.handleErrors.invalidFileType")}
                                    type="text"
                                    multiline
                                    InputProps={{
                                        disableUnderline: true
                                    }}
                                />
                            </Box>
                        </Grid>
                    }
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "10px"}}>
                <Button color={"accent"}
                        onClick={() => {
                            reset(defaultValues());
                            props.onClose();
                        }}>{t('leasePlan.modal.buttons.close')}</Button>
            </DialogActions>

        </Dialog>
    )
}

export default UploadLeasePlanModal;